import {
  CONTAINER_TYPE_OPTIONS,
  type Country,
  Currency,
  CURRENCY_OPTIONS,
  DEFAULT_CONTAINER_TYPE,
  isFieldErrorResponse,
  type PointToPointTransportCostBreakdownResultDTO,
  PortTypes,
  useApiTransportPricesV1EstimateCreate,
} from 'api';
import { formatDate } from 'date';
import {
  AddressAutocomplete,
  Form,
  IncotermSelectField,
  type InferType,
  SelectField,
  TextField,
  useForm,
  yupResolver,
} from 'form';
import { PortAutocomplete } from 'form';
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  formatCurrency,
  Grid,
  Page,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from 'ui';

import { displayLocalCostCategory, displayMissingCost, displayTransportCostSource } from '../../utils';
import { createTransportCostsEstimateSchema } from './schema';

type FormValues = InferType<typeof createTransportCostsEstimateSchema>;

const DEFAULT_VALUES: Partial<FormValues> = {
  container_type: DEFAULT_CONTAINER_TYPE,
  currency: Currency.GBP,
  demand_incoterm: 'DDP',
  nearest_city_max_radius: 25,
  supply_incoterm: 'EXW',
};

const getIncludedDisplayedName = (isIncluded: boolean) => (isIncluded ? 'Yes' : 'No');

const displayCostInTargetAndOriginalCurrency = (
  originalCost: string,
  originalCurrency: Currency,
  targetCost: string,
  targetCurrency: Currency
) => {
  if (originalCurrency === targetCurrency) {
    return formatCurrency(originalCost, { currencyCode: originalCurrency });
  }
  return `${formatCurrency(targetCost, { currencyCode: targetCurrency })} (${formatCurrency(originalCost, { currencyCode: originalCurrency })})`;
};

interface SeparatorRowProps {
  title: string;
}

const SeparatorRow = ({ title }: SeparatorRowProps) => (
  <TableRow sx={{ bgcolor: 'lightgrey' }}>
    <TableCell>
      <Typography fontWeight="bold">{title}</Typography>
    </TableCell>
    <TableCell />
    <TableCell />
  </TableRow>
);

export const TransportCostsEstimator = () => {
  const form = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(createTransportCostsEstimateSchema),
  });
  const { setError, formState } = form;
  const { errors } = formState;
  const [responseData, setResponseData] = useState<PointToPointTransportCostBreakdownResultDTO | null>(null);
  const { isPending, mutate: createTransportPrices } = useApiTransportPricesV1EstimateCreate({
    mutation: {
      onError(error) {
        const errorMessages: string[] = [];
        if (isFieldErrorResponse(error)) {
          Object.keys(error.response?.data).forEach((field) => {
            setError(field as keyof FormValues, { message: error.response.data[field] });
            errorMessages.push(error.response.data[field]);
          });
        }
        setError('root', { message: errorMessages.join(', ') || 'An error occurred' });

        setResponseData(null);
      },
      async onSuccess(data) {
        setResponseData(data);
      },
    },
  });

  const onSubmit = async (values: FormValues) => {
    createTransportPrices({
      data: {
        container_type: values.container_type,
        currency: values.currency,
        demand_incoterm: values.demand_incoterm,
        destination: values.destination
          ? {
              country: (values.destination.country_code as Country) || '',
              full_address: values.destination.full_address || '',
              lat: values.destination.latitude || 0,
              lon: values.destination.longitude || 0,
            }
          : undefined,
        nearest_city_max_radius: values.nearest_city_max_radius.toString(),
        origin: values.origin
          ? {
              country: (values.origin.country_code as Country) || '',
              full_address: values.origin.full_address || '',
              lat: values.origin.latitude || 0,
              lon: values.origin.longitude || 0,
            }
          : undefined,
        port_of_discharge: values.port_of_discharge || undefined,
        port_of_loading: values.port_of_loading || undefined,
        supply_incoterm: values.supply_incoterm,
      },
    });
  };
  return (
    <Page>
      <Page.Title title="Transport Costs Estimator" />
      <Form {...form} onSubmit={onSubmit}>
        {errors.root ? <Alert severity="error">{String(errors.root.message)}</Alert> : null}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" my={1}>
              Supply
            </Typography>
            <Grid container spacing={2} direction="column">
              <Grid item xs={12}>
                <AddressAutocomplete name="origin" label="Origin address" />
              </Grid>
              <Grid item xs={12}>
                <PortAutocomplete name="port_of_loading" label="Port of loading" type={PortTypes.REG} />
              </Grid>
              <Grid item xs={12}>
                <IncotermSelectField name="supply_incoterm" label="Incoterm" fullWidth />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" my={1}>
              Demand
            </Typography>
            <Grid container spacing={2} direction="column">
              <Grid item xs={12}>
                <AddressAutocomplete name="destination" label="Destination address" />
              </Grid>
              <Grid item xs={12}>
                <PortAutocomplete name="port_of_discharge" label="Port of discharge" type={PortTypes.REG} />
              </Grid>
              <Grid item xs={12}>
                <IncotermSelectField name="demand_incoterm" label="Incoterm" fullWidth />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" my={1}>
              Extra options
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField name="nearest_city_max_radius" label="Nearest city max radius" type="number" fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <SelectField label="Currency" name="currency" options={CURRENCY_OPTIONS} />
          </Grid>
          <Grid item xs={12} md={4}>
            <SelectField label="Container Type" name="container_type" options={CONTAINER_TYPE_OPTIONS} />
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="end" alignItems="center" paddingTop={2}>
          <Button type="submit" size="large" loading={isPending}>
            Estimate
          </Button>
        </Box>
      </Form>

      <Divider sx={{ my: 2 }} />

      {responseData ? (
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight="bold">
            Transport prices
          </Typography>

          <Grid container justifyContent="start">
            <Grid item xs={12} md={10}>
              <Table>
                <TableBody>
                  {responseData.missing_costs && responseData.missing_costs.length > 0 ? (
                    <>
                      <SeparatorRow title="Missing Costs" />
                      {responseData.missing_costs.map((cost) => (
                        <TableRow key={cost}>
                          <TableCell sx={{ bgcolor: 'orange' }}>
                            <Typography>{displayMissingCost(cost)}</Typography>
                          </TableCell>
                          <TableCell />
                          <TableCell />
                        </TableRow>
                      ))}
                    </>
                  ) : null}
                  {responseData.is_from_expired_quote ? (
                    <>
                      <SeparatorRow title={'Other Warnings'} />
                      <TableRow>
                        <TableCell sx={{ bgcolor: 'orange' }}>
                          <Typography>
                            An expired quote from up to 3 months old was used for this estimation, while the normal
                            value is 1 month. Please consider this a less accurate indication.
                          </Typography>
                        </TableCell>
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    </>
                  ) : null}
                  {responseData.local_origin_costs && responseData.local_origin_costs.length > 0 ? (
                    <>
                      <SeparatorRow title="Local Origin Costs" />
                      {responseData.local_origin_costs.map((cost) => (
                        <TableRow key={cost.category}>
                          <TableCell>
                            <Typography fontWeight="bold">{displayLocalCostCategory(cost.category)}:</Typography>
                          </TableCell>
                          <TableCell sx={{ bgcolor: cost.is_included_in_total ? 'lightgreen' : 'lightsalmon' }}>
                            <Typography>
                              {displayCostInTargetAndOriginalCurrency(
                                cost.cost,
                                cost.currency,
                                cost.cost_in_target_currency,
                                responseData.target_currency
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography>{displayTransportCostSource(cost.source)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : null}
                  {responseData.origin_road_cost && responseData.origin_road_cost.cost_in_target_currency ? (
                    <>
                      <SeparatorRow title="Origin Road" />

                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">Cost:</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.origin_road_cost.is_included_in_total ? 'lightgreen' : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.origin_road_cost.cost,
                              responseData.origin_road_cost.currency,
                              responseData.origin_road_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{displayTransportCostSource(responseData.origin_road_cost.source)}</Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  ) : null}
                  {responseData.origin_road_cost?.nearest_city ? (
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">Origin nearest city:</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{responseData.origin_road_cost.nearest_city}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{responseData.origin_road_cost.nearest_city_distance}km away</Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {responseData.origin_terminal_handling_cost !== null ||
                  responseData.ocean_freight_cost ||
                  responseData.destination_terminal_handling_cost ? (
                    <SeparatorRow title="Ocean Freight & Port Charges" />
                  ) : null}

                  {responseData.used_port_of_loading ? (
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">Origin port:</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {responseData.used_port_of_loading.name} ({responseData.used_port_of_loading.unlocode})
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {responseData.origin_terminal_handling_cost !== null &&
                    responseData.origin_terminal_handling_cost.cost_in_target_currency !== null && (
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">OTHC:</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.origin_terminal_handling_cost.is_included_in_total
                              ? 'lightgreen'
                              : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.origin_terminal_handling_cost.cost,
                              responseData.origin_terminal_handling_cost.currency,
                              responseData.origin_terminal_handling_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {displayTransportCostSource(responseData.origin_terminal_handling_cost.source)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                  {responseData.ocean_freight_cost ? (
                    <>
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">Is OTHC included in Ocean freight rate:</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {getIncludedDisplayedName(
                              responseData.ocean_freight_cost.is_origin_terminal_handling_cost_included
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">Ocean freight cost:</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.ocean_freight_cost.is_included_in_total
                              ? 'lightgreen'
                              : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.ocean_freight_cost.cost,
                              responseData.ocean_freight_cost.currency,
                              responseData.ocean_freight_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{displayTransportCostSource(responseData.ocean_freight_cost.source)}</Typography>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">Ocean freight quoted at:</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{formatDate(responseData.ocean_freight_cost.quoted_on)}</Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </>
                  ) : null}

                  {responseData.used_port_of_discharge ? (
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">Destination port:</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {responseData.used_port_of_discharge.name} ({responseData.used_port_of_discharge.unlocode})
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  ) : null}

                  {responseData.destination_terminal_handling_cost !== null &&
                    responseData.destination_terminal_handling_cost.cost_in_target_currency !== null && (
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">DTHC:</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.destination_terminal_handling_cost.is_included_in_total
                              ? 'lightgreen'
                              : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.destination_terminal_handling_cost.cost,
                              responseData.destination_terminal_handling_cost.currency,
                              responseData.destination_terminal_handling_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {displayTransportCostSource(responseData.destination_terminal_handling_cost.source)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                  {responseData.ocean_freight_cost ? (
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">Is DTHC included in Ocean freight rate:</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {getIncludedDisplayedName(
                            responseData.ocean_freight_cost.is_destination_terminal_handling_cost_included
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  ) : null}

                  {responseData.destination_road_cost && responseData.destination_road_cost.cost_in_target_currency ? (
                    <>
                      <SeparatorRow title="Destination Road" />
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">Cost:</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.destination_road_cost.is_included_in_total
                              ? 'lightgreen'
                              : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.destination_road_cost.cost,
                              responseData.destination_road_cost.currency,
                              responseData.destination_road_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {displayTransportCostSource(responseData.destination_road_cost.source)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  ) : null}
                  {responseData.destination_road_cost?.nearest_city ? (
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">Nearest city:</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{responseData.destination_road_cost.nearest_city}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{responseData.destination_road_cost.nearest_city_distance}km away</Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {responseData.direct_land_cost ? (
                    <>
                      <SeparatorRow title="Direct Land Transport" />
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">Cost:</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.direct_land_cost.is_included_in_total ? 'lightgreen' : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.direct_land_cost.cost,
                              responseData.direct_land_cost.currency,
                              responseData.direct_land_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{displayTransportCostSource(responseData.direct_land_cost.source)}</Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  ) : null}
                  {responseData.local_destination_costs && responseData.local_destination_costs.length > 0 ? (
                    <>
                      <SeparatorRow title="Local Destination Costs" />
                      {responseData.local_destination_costs.map((cost) => (
                        <TableRow key={cost.category}>
                          <TableCell>
                            <Typography fontWeight="bold">{displayLocalCostCategory(cost.category)}:</Typography>
                          </TableCell>
                          <TableCell sx={{ bgcolor: cost.is_included_in_total ? 'lightgreen' : 'lightsalmon' }}>
                            <Typography>
                              {displayCostInTargetAndOriginalCurrency(
                                cost.cost,
                                cost.currency,
                                cost.cost_in_target_currency,
                                responseData.target_currency
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography>{displayTransportCostSource(cost.source)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : null}
                  {responseData.e2e_cost && responseData.e2e_cost.cost_in_target_currency ? (
                    <>
                      <SeparatorRow title="E2E Quote (Sharp Quote)" />
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">Cost:</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.e2e_cost.is_included_in_total ? 'lightgreen' : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.e2e_cost.cost,
                              responseData.e2e_cost.currency,
                              responseData.e2e_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{displayTransportCostSource(responseData.e2e_cost.source)}</Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  ) : null}
                  <SeparatorRow title="Total" />
                  <TableRow>
                    <TableCell>
                      <Typography fontWeight="bold" fontSize={24}>
                        Total cost:
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" fontSize={24}>
                        {formatCurrency(responseData.total_cost_in_target_currency, {
                          currencyCode: responseData.target_currency,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        </Stack>
      ) : null}
    </Page>
  );
};
